import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { BrandList } from 'common/models/brand.model';
import { AccountAdminBrandsItemsEntity, AuthorizedBrand, Company } from 'private/app/models/account-admin-search';
import { AccountAdminExtendedService } from 'private/app/services/account-admin/account-admin-extended.service';
import { AccountAdminService } from 'private/app/services/account-admin/account-admin.service';
import { IdmService } from 'private/app/services/idm.service';
import { Subject } from 'rxjs';

@Component({
    selector: 'hvac-authorized-brands',
    templateUrl: './authorized-brands.component.html',
    styleUrls: ['./authorized-brands.component.scss']
})
export class AuthorizedBrandsComponent implements OnInit, OnChanges {
    @Input() authorizedBrands: AuthorizedBrand[];
    @Input() editCompanyInformation: boolean;
    @Input() company: Company;
    @Output() public selectedBrands = new EventEmitter<AuthorizedBrand[]>();
    @Output() public isBrandSelected = new EventEmitter<boolean>();

    ngOnDestroy$ = new Subject();
    public addBrands: boolean;
    public userBrandList: BrandList[] = [];
    public brandsList: string[] = [];
    public hvacCompanyId: string;

    constructor(
        protected idmService: IdmService,
        protected accountAdminExtendedService: AccountAdminExtendedService,
        protected adminService: AccountAdminService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.authorizedBrands?.currentValue) {
            this.initBrandMapping();
        }
        this.authorizedBrands?.forEach((brand) => {
            if (this.brandsList.includes(brand.code)) {
                brand.canEdit = false;
            }
        });
    }

    ngOnInit(): void {
        this.initBrandMapping();
        this.hvacCompanyId = this.company?.hvacCompanyId;
        this.getHeirarchicalBrands(this.hvacCompanyId);
    }

    getHeirarchicalBrands(hvacCompanyId: string) {
        this.accountAdminExtendedService.getAssociatedBrands(hvacCompanyId).subscribe((response) => {
            this.brandsList = response.map((brand) => brand.brandCode.trim() || '');
        });
        this.authorizedBrands?.forEach((brand) => {
            if (this.brandsList.includes(brand.code)) {
                brand.canEdit = false;
            }
        });
    }

    updateBrandEditability() {
        this.authorizedBrands?.forEach((brand) => {
            brand.canEdit = !this.associatedBrandsCheck(brand.code);
        });
    }

    associatedBrandsCheck(brandCode: string) {
        return (this.brandsList.includes(brandCode));
    }

    showBrandlist() {
        this.addBrands = !this.addBrands;
    }

    addBrand(event: Event, brand: BrandList) {
        const isChecked = (event.target as unknown as HTMLInputElement).checked;

        this.userBrandList?.forEach((userBrand) => {
            if (brand.brandCode === userBrand.brandCode) {
                userBrand.selected = isChecked;

                if (isChecked) {
                    const existingBrand = this.authorizedBrands.find(
                        (authorizedBrand) => authorizedBrand.code === brand.brandCode
                    );

                    if (!existingBrand) {
                        this.authorizedBrands.push({
                            name: brand.brandName,
                            code: brand.brandCode,
                            active: true,
                            canEdit: !this.associatedBrandsCheck(brand.brandCode)
                        });
                    }
                }
                else {
                    this.authorizedBrands = this.authorizedBrands.filter(
                        (authorizedBrand) => authorizedBrand.code !== brand.brandCode
                    );
                }
            }
        });

        this.selectedBrands.next(this.authorizedBrands);
        this.isBrandSelected.emit(this.authorizedBrands.length > 0);
    }

    removeBrand(brand: AuthorizedBrand) {
        this.userBrandList?.forEach((userBrand) => {
            if (brand.code === userBrand.brandCode) {
                userBrand.selected = false;
            }
        });

        this.authorizedBrands = this.authorizedBrands.filter(
            (authorizedBrand) => brand.code !== authorizedBrand.code
        );

        this.selectedBrands.next(this.authorizedBrands);
        this.isBrandSelected.emit(this.authorizedBrands.length > 0);
    }

    trackBybrandCode(_index: number, brand: BrandList) {
        return brand.brandCode;
    }

    trackByCode(_index: number, brand: AuthorizedBrand) {
        return brand.code;
    }

    private initBrandMapping() {
        this.accountAdminExtendedService.getBrands(false).subscribe((brandList) => {
            this.authorizedBrands = this.authorizedBrands?.map((authorizedBrand) => {
                const matchingBrand = brandList.find(
                    (brand) => brand.code === authorizedBrand.code
                );

                return {
                    ...authorizedBrand,
                    name: matchingBrand?.name?.trim() || authorizedBrand.name?.trim() || ''
                };
            });

            const flattenedBrands: BrandList[] = brandList.map((option:AccountAdminBrandsItemsEntity) => ({
                brandName: option.name,
                brandCode: option.code
            }));

            flattenedBrands.forEach((availableBrand) => {
                const matchingAuthorizedBrand = this.authorizedBrands?.find(
                    (existingBrand) => existingBrand.code === availableBrand.brandCode
                );

                if (matchingAuthorizedBrand) {
                    availableBrand.selected = true;
                    matchingAuthorizedBrand.canEdit = true;
                }
            });

            this.userBrandList = flattenedBrands;
        });
    }
}
